import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { dbSelect, dbInsert, dbUpdate, dbDelete } from '../../lib/supabase';
import { blogSeedData } from '../../data/blogSeedData';

const BlogAdminPanel = ({ activeSection = 'posts', editId = null }) => {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [comments, setComments] = useState([]);
  const [categories, setCategories] = useState([
    'Software Development', 'Website Development', 'Web Applications', 'Mobile Applications',
    'AI', 'AI Agents', 'Automation', 'React', 'Next.js', 'Python', 'Node.js',
    'Flask', 'Cloud', 'AWS', 'CRM', 'ERP', 'Restaurant Software', 'Healthcare Software',
    'SEO', 'AEO', 'GEO', 'Google Business', 'Startups', 'Technology', 'Business Growth'
  ]);
  const [tags, setTags] = useState(['React', 'Node.js', 'AI', 'SaaS', 'ERP', 'GEO', 'SEO', 'Startups']);
  const [loading, setLoading] = useState(false);

  // Form State
  const [form, setForm] = useState({
    id: '',
    title: '',
    slug: '',
    category: 'Software Development',
    tags: [],
    author: 'Giridharan Sivaramakrishnan',
    content: '',
    status: 'draft',
    isFeatured: false,
    isSticky: false,
    metaTitle: '',
    metaDescription: '',
    focusKeyword: '',
    secondaryKeywords: '',
    canonicalUrl: '',
    ogImage: 'https://bytepairs.com/android-chrome-512x512.png',
    twitterCard: 'summary_large_image',
    schemaType: 'Article'
  });

  // Settings State
  const [settings, setSettings] = useState({
    defaultAuthor: 'Giridharan Sivaramakrishnan',
    defaultMetaDesc: 'Custom software engineering insights from Byte Pairs.',
    defaultCategory: 'Software Development',
    autoSlug: true,
    autoReadingTime: true,
    autoSitemap: true,
    autoRSS: true
  });

  const [activeFaq, setActiveFaq] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [newTag, setNewTag] = useState('');
  const [validationError, setValidationError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Load all dashboard content
  useEffect(() => {
    loadData();
    const savedSettings = localStorage.getItem('bp_blog_settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Handle Edit routing data fetch
  useEffect(() => {
    if (editId && blogs.length > 0) {
      const match = blogs.find(b => b.id === editId || b.slug === editId);
      if (match) {
        setForm({
          ...match,
          tags: Array.isArray(match.tags) ? match.tags : (match.tags || '').split(',').map(t => t.trim())
        });
      }
    }
  }, [editId, blogs]);

  // Autosave setup (every 15 seconds)
  useEffect(() => {
    if (activeSection === 'new' || activeSection === 'edit') {
      const interval = setInterval(() => {
        localStorage.setItem('bp_blog_autosave', JSON.stringify(form));
        console.log("Blog progress autosaved.");
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [form, activeSection]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await dbSelect('blogs', 'publishedDate', false);
      setBlogs(data || blogSeedData);

      const { data: comms } = await dbSelect('blog_comments', 'createdAt', false);
      setComments(comms || []);

      const { data: cats } = await dbSelect('blog_categories', 'name', true);
      if (cats && cats.length > 0) {
        setCategories(cats.map(c => c.name));
      }
      
      const { data: tgs } = await dbSelect('blog_tags', 'name', true);
      if (tgs && tgs.length > 0) {
        setTags(tgs.map(t => t.name));
      }
    } catch (err) {
      console.error("Error loading admin datasets:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === 'checkbox' ? checked : value;
    
    setForm(prev => {
      const updated = { ...prev, [name]: newVal };
      // Auto Slug generation if active
      if (name === 'title' && settings.autoSlug) {
        updated.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        updated.canonicalUrl = `https://bytepairs.com/blog/${updated.slug}`;
        updated.metaTitle = `${value} | Byte Pairs Blog`;
      }
      return updated;
    });
  };

  const handleTagToggle = (tagName) => {
    setForm(prev => {
      const tagsList = [...prev.tags];
      const idx = tagsList.indexOf(tagName);
      if (idx !== -1) {
        tagsList.splice(idx, 1);
      } else {
        tagsList.push(tagName);
      }
      return { ...prev, tags: tagsList };
    });
  };

  // Calculate Reading Time based on words
  const calculateReadingTime = (text) => {
    if (!text) return 1;
    const words = text.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(words / 200));
  };

  // Submit Blog Post
  const handleSavePost = async (e) => {
    e.preventDefault();
    setValidationError('');
    setSuccessMsg('');

    // Form Slug checks
    if (!form.slug) {
      setValidationError("Slug is required.");
      return;
    }

    // Duplicate check
    const duplicate = blogs.find(b => b.slug === form.slug && b.id !== form.id);
    if (duplicate) {
      setValidationError("A post with this URL slug already exists. Please choose a unique slug.");
      return;
    }

    const calculatedTime = calculateReadingTime(form.content);
    const postPayload = {
      ...form,
      readingTime: calculatedTime,
      publishedDate: form.status === 'published' ? (form.publishedDate || new Date().toISOString()) : null,
      lastUpdated: new Date().toISOString()
    };

    try {
      if (activeSection === 'edit') {
        await dbUpdate('blogs', form.id, postPayload);
        setSuccessMsg("Article updated successfully!");
      } else {
        const { data } = await dbInsert('blogs', {
          ...postPayload,
          views: 0,
          likes: 0
        });
        setSuccessMsg("New article published/saved successfully!");
      }
      localStorage.removeItem('bp_blog_autosave');
      setTimeout(() => {
        loadData();
        navigate('/admin/blog');
      }, 1500);
    } catch (err) {
      setValidationError("Error saving post to database: " + err.message);
    }
  };

  // Delete Blog Post
  const handleDeletePost = async (id) => {
    if (!window.confirm("Permanently delete this blog article?")) return;
    try {
      await dbDelete('blogs', id);
      setBlogs(prev => prev.filter(b => b.id !== id));
    } catch (err) {
      alert("Error deleting article: " + err.message);
    }
  };

  // Save Settings
  const handleSaveSettings = (e) => {
    e.preventDefault();
    localStorage.setItem('bp_blog_settings', JSON.stringify(settings));
    setSuccessMsg("Settings saved locally!");
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  // Manage Comments
  const handleCommentStatus = async (id, newStatus) => {
    try {
      if (newStatus === 'delete') {
        await dbDelete('blog_comments', id);
        setComments(prev => prev.filter(c => c.id !== id));
      } else {
        await dbUpdate('blog_comments', id, { status: newStatus });
        setComments(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
      }
    } catch (err) {
      alert("Error updating comment: " + err.message);
    }
  };

  // Add category/tag
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCat || categories.includes(newCat)) return;
    try {
      await dbInsert('blog_categories', { name: newCat });
      setCategories(prev => [...prev, newCat]);
      setNewCat('');
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTag || tags.includes(newTag)) return;
    try {
      await dbInsert('blog_tags', { name: newTag });
      setTags(prev => [...prev, newTag]);
      setNewTag('');
    } catch (err) {
      console.error(err);
    }
  };

  // Restore autosave
  const handleRestoreAutosave = () => {
    const saved = localStorage.getItem('bp_blog_autosave');
    if (saved) {
      setForm(JSON.parse(saved));
      alert("Autosave draft restored successfully.");
    } else {
      alert("No autosaved progress found.");
    }
  };

  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-3xl p-6 lg:p-8">
      {/* Tab Inner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-6 border-b border-white/5 mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-black text-white capitalize">Blog Manager Dashboard</h2>
          <p className="text-slate-400 text-xs mt-1">Configure organic search tags, local schemas, and drafts for Byte Pairs.</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => navigate('/admin/blog')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'posts' ? 'bg-primary text-white shadow-brand-sm' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="FileText" size={14} />
            <span>Posts</span>
          </button>
          <button 
            onClick={() => {
              setForm({
                id: Math.random().toString(36).substring(2, 11),
                title: '',
                slug: '',
                category: settings.defaultCategory,
                tags: [],
                author: settings.defaultAuthor,
                content: '',
                status: 'draft',
                isFeatured: false,
                isSticky: false,
                metaTitle: '',
                metaDescription: settings.defaultMetaDesc,
                focusKeyword: '',
                secondaryKeywords: '',
                canonicalUrl: '',
                ogImage: 'https://bytepairs.com/android-chrome-512x512.png',
                twitterCard: 'summary_large_image',
                schemaType: 'Article'
              });
              navigate('/admin/blog/new');
            }}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'new' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="Plus" size={14} />
            <span>Write Article</span>
          </button>
          <button 
            onClick={() => navigate('/admin/blog/categories')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'categories' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="Folder" size={14} />
            <span>Categories</span>
          </button>
          <button 
            onClick={() => navigate('/admin/blog/tags')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'tags' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="Tag" size={14} />
            <span>Tags</span>
          </button>
          <button 
            onClick={() => navigate('/admin/blog/comments')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'comments' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="MessageSquare" size={14} />
            <span>Comments</span>
          </button>
          <button 
            onClick={() => navigate('/admin/blog/settings')}
            className={`px-4 py-2 rounded-xl text-xs font-semibold flex items-center space-x-1.5 transition-all ${activeSection === 'settings' ? 'bg-primary text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
          >
            <Icon name="Settings" size={14} />
            <span>Settings</span>
          </button>
        </div>
      </div>

      {/* Success/Error Alerts */}
      {validationError && (
        <div className="p-4 bg-red-500/10 border border-red-500/25 rounded-2xl text-red-400 text-sm mb-6 flex items-start space-x-3">
          <Icon name="AlertTriangle" size={18} className="flex-shrink-0 mt-0.5" />
          <span>{validationError}</span>
        </div>
      )}
      {successMsg && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl text-emerald-400 text-sm mb-6 flex items-start space-x-3">
          <Icon name="CheckCircle" size={18} className="flex-shrink-0 mt-0.5" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* TABS BODY CONTENT */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-20 space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 text-xs">Loading database records...</p>
        </div>
      ) : (
        <>
          {/* Section: Posts List */}
          {activeSection === 'posts' && (
            <div className="overflow-x-auto border border-white/5 rounded-2xl bg-slate-950/20">
              <table className="w-full text-left text-sm text-slate-300 border-collapse">
                <thead>
                  <tr className="bg-slate-900 border-b border-white/5 text-slate-400 font-semibold">
                    <th className="p-4">Title / Category</th>
                    <th className="p-4">Slug URL</th>
                    <th className="p-4 text-center">Status</th>
                    <th className="p-4 text-center">Views / Likes</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((b) => (
                    <tr key={b.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-white line-clamp-1">{b.title}</div>
                        <div className="text-[10px] text-slate-400 uppercase font-semibold mt-1 flex items-center space-x-2">
                          <span className="bg-slate-800 text-slate-300 px-2 py-0.5 rounded">{b.category}</span>
                          {b.isFeatured && <span className="bg-primary/20 text-primary px-2 py-0.5 rounded">Featured</span>}
                          {b.isSticky && <span className="bg-accent/20 text-accent px-2 py-0.5 rounded">Sticky</span>}
                        </div>
                      </td>
                      <td className="p-4 text-xs text-slate-400 font-mono">/blog/{b.slug}</td>
                      <td className="p-4 text-center">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${b.status === 'published' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                          {b.status || 'published'}
                        </span>
                      </td>
                      <td className="p-4 text-center text-xs">
                        <span className="text-slate-300">{b.views || 0}</span> / <span className="text-slate-400">{b.likes || 0}</span>
                      </td>
                      <td className="p-4 text-right space-x-2">
                        <button 
                          onClick={() => navigate(`/admin/blog/edit/${b.id}`)}
                          className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Icon name="Edit" size={14} />
                        </button>
                        <button 
                          onClick={() => handleDeletePost(b.id)}
                          className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Icon name="Trash" size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Section: Editor (New / Edit) */}
          {(activeSection === 'new' || activeSection === 'edit') && (
            <form onSubmit={handleSavePost} className="space-y-6">
              
              {/* Form Actions Header */}
              <div className="flex items-center justify-between pb-4 border-b border-white/5">
                <h3 className="text-lg font-bold text-white">
                  {activeSection === 'edit' ? 'Edit Article details' : 'Draft New Article'}
                </h3>
                <div className="flex space-x-2">
                  <button 
                    type="button" 
                    onClick={handleRestoreAutosave}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold flex items-center space-x-1"
                  >
                    <Icon name="RotateCcw" size={12} />
                    <span>Restore Draft</span>
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setPreviewMode(!previewMode)}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold flex items-center space-x-1"
                  >
                    <Icon name="Eye" size={12} />
                    <span>{previewMode ? 'Edit Mode' : 'Preview Layout'}</span>
                  </button>
                </div>
              </div>

              {previewMode ? (
                /* SIDE-BY-SIDE MARKDOWN PREVIEW */
                <div className="p-6 bg-slate-950 border border-white/5 rounded-2xl min-h-[300px] text-left">
                  <span className="text-[10px] uppercase bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-bold">{form.category}</span>
                  <h1 className="text-3xl font-black text-white mt-4 mb-2">{form.title || 'Untitled Article'}</h1>
                  <p className="text-slate-500 text-xs mb-6">Author: {form.author} | URL: /blog/{form.slug}</p>
                  <div className="border-t border-white/5 pt-6 text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {form.content || 'Start typing in markdown to see content preview here.'}
                  </div>
                </div>
              ) : (
                /* FULL EDITOR FORM */
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 text-left">
                  
                  {/* Left Column: Post fields */}
                  <div className="lg:col-span-8 space-y-6">
                    <div>
                      <label className="block text-slate-400 text-xs font-bold mb-2 uppercase">Article Title</label>
                      <input 
                        type="text" 
                        name="title"
                        required
                        value={form.title} 
                        onChange={handleInputChange}
                        placeholder="e.g. Building Scalable AI Agents with React"
                        className="w-full px-4 py-3 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none focus:border-primary"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-slate-400 text-xs font-bold mb-2 uppercase">Slug URL</label>
                        <input 
                          type="text" 
                          name="slug"
                          required
                          value={form.slug} 
                          onChange={handleInputChange}
                          placeholder="building-scalable-ai-agents"
                          className="w-full px-4 py-3 bg-slate-950 border border-white/8 rounded-xl text-white text-xs font-mono focus:outline-none focus:border-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 text-xs font-bold mb-2 uppercase">Author</label>
                        <input 
                          type="text" 
                          name="author"
                          required
                          value={form.author} 
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none focus:border-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-slate-400 text-xs font-bold mb-2 uppercase">Cover Image URL</label>
                      <input 
                        type="text" 
                        name="ogImage"
                        value={form.ogImage} 
                        onChange={handleInputChange}
                        placeholder="e.g. https://bytepairs.com/assets/my-cover.jpg"
                        className="w-full px-4 py-3 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none focus:border-primary"
                      />
                    </div>

                    <div>
                      <label className="block text-slate-400 text-xs font-bold mb-2 uppercase">Content Markdown Body</label>
                      <div className="text-[10px] text-slate-500 mb-2 font-mono">Supports # Header, ## Subheader, &gt; Blockquotes, | Table Columns |</div>
                      <textarea 
                        name="content"
                        required
                        rows={14}
                        value={form.content} 
                        onChange={handleInputChange}
                        placeholder="# Heading 1&#10;&#10;Type your article content here in markdown format..."
                        className="w-full px-4 py-3 bg-slate-950 border border-white/8 rounded-xl text-white text-sm font-mono focus:outline-none focus:border-primary leading-relaxed"
                      />
                    </div>
                  </div>

                  {/* Right Column: SEO details & Categorizations */}
                  <div className="lg:col-span-4 space-y-6">
                    
                    {/* Publishing details */}
                    <div className="p-5 bg-slate-950/40 border border-white/5 rounded-2xl space-y-4">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">Publishing Status</h4>
                      
                      <div>
                        <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">Status</label>
                        <select 
                          name="status"
                          value={form.status}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-xs focus:outline-none"
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-between text-xs pt-2">
                        <label className="text-slate-400">Set as Featured Post</label>
                        <input 
                          type="checkbox" 
                          name="isFeatured"
                          checked={form.isFeatured}
                          onChange={handleInputChange}
                          className="w-4 h-4 accent-primary"
                        />
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <label className="text-slate-400">Set as Sticky Post</label>
                        <input 
                          type="checkbox" 
                          name="isSticky"
                          checked={form.isSticky}
                          onChange={handleInputChange}
                          className="w-4 h-4 accent-primary"
                        />
                      </div>
                    </div>

                    {/* Category Selection */}
                    <div className="p-5 bg-slate-950/40 border border-white/5 rounded-2xl space-y-3">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">Categories & Tags</h4>
                      <div>
                        <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">Primary Category</label>
                        <select 
                          name="category"
                          value={form.category}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-white text-xs focus:outline-none"
                        >
                          {categories.map((cat, idx) => (
                            <option key={idx} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Tags list checkboxes */}
                      <div>
                        <label className="block text-slate-500 text-[10px] font-bold mb-2 uppercase">Tags</label>
                        <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto p-2 bg-slate-950 border border-white/8 rounded-lg">
                          {tags.map((t, idx) => {
                            const active = form.tags.includes(t);
                            return (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleTagToggle(t)}
                                className={`text-[10px] px-2 py-1 rounded transition-colors ${active ? 'bg-primary text-white font-bold' : 'bg-slate-900 text-slate-400'}`}
                              >
                                #{t}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* SEO Metadata Fields */}
                    <div className="p-5 bg-slate-950/40 border border-white/5 rounded-2xl space-y-4">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400">GEO / SEO Optimizations</h4>

                      <div>
                        <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">SEO Title Meta</label>
                        <input 
                          type="text"
                          name="metaTitle"
                          value={form.metaTitle}
                          onChange={handleInputChange}
                          placeholder="Title tag value"
                          className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-xs text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">Meta Description</label>
                        <textarea 
                          name="metaDescription"
                          rows={2.5}
                          value={form.metaDescription}
                          onChange={handleInputChange}
                          placeholder="Rich, summary description"
                          className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-xs text-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">Focus Key</label>
                          <input 
                            type="text"
                            name="focusKeyword"
                            value={form.focusKeyword}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-xs text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-500 text-[10px] font-bold mb-1 uppercase">Schema Type</label>
                          <select
                            name="schemaType"
                            value={form.schemaType}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 bg-slate-900 border border-white/10 rounded-lg text-xs text-white"
                          >
                            <option value="Article">Article</option>
                            <option value="TechArticle">TechArticle</option>
                            <option value="NewsArticle">NewsArticle</option>
                          </select>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              )}

              {/* Form footer actions */}
              {!previewMode && (
                <div className="pt-6 border-t border-white/5 flex items-center justify-end space-x-3">
                  <button 
                    type="button"
                    onClick={() => navigate('/admin/blog')}
                    className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-semibold"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="px-6 py-2.5 bg-primary text-white rounded-xl text-xs font-semibold shadow-brand-sm"
                  >
                    Save Changes & Sync
                  </button>
                </div>
              )}

            </form>
          )}

          {/* Section: Categories Manager */}
          {activeSection === 'categories' && (
            <div className="max-w-md mx-auto space-y-6 text-left">
              <form onSubmit={handleAddCategory} className="flex gap-2">
                <input
                  type="text"
                  required
                  placeholder="New Category Name"
                  value={newCat}
                  onChange={(e) => setNewCat(e.target.value)}
                  className="px-4 py-2.5 bg-slate-950 border border-white/8 rounded-xl text-sm focus:outline-none focus:border-primary flex-grow text-white"
                />
                <button type="submit" className="bg-primary text-white px-5 py-2.5 rounded-xl text-xs font-semibold">
                  Add Category
                </button>
              </form>

              <div className="bg-slate-950/20 border border-white/5 rounded-2xl p-5 space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-4 border-b border-white/5 pb-2">Active Categories</h4>
                <div className="divide-y divide-white/5">
                  {categories.map((cat, idx) => (
                    <div key={idx} className="flex justify-between py-2.5 text-sm text-slate-300">
                      <span>{cat}</span>
                      <span className="text-[10px] text-slate-500 font-semibold font-mono">Custom Category</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Section: Tags Manager */}
          {activeSection === 'tags' && (
            <div className="max-w-md mx-auto space-y-6 text-left">
              <form onSubmit={handleAddTag} className="flex gap-2">
                <input
                  type="text"
                  required
                  placeholder="New Tag Name"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className="px-4 py-2.5 bg-slate-950 border border-white/8 rounded-xl text-sm focus:outline-none focus:border-primary flex-grow text-white"
                />
                <button type="submit" className="bg-primary text-white px-5 py-2.5 rounded-xl text-xs font-semibold">
                  Add Tag
                </button>
              </form>

              <div className="bg-slate-950/20 border border-white/5 rounded-2xl p-5 space-y-2">
                <h4 className="font-bold text-xs uppercase tracking-wider text-slate-500 mb-4 border-b border-white/5 pb-2">Active Tag Cloud</h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-slate-800 text-slate-300 px-3 py-1.5 rounded-lg border border-white/5">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Section: Comments Manager */}
          {activeSection === 'comments' && (
            <div className="overflow-x-auto border border-white/5 rounded-2xl bg-slate-950/20">
              <table className="w-full text-left text-sm text-slate-300 border-collapse">
                <thead>
                  <tr className="bg-slate-900 border-b border-white/5 text-slate-400 font-semibold">
                    <th className="p-4">Author / Date</th>
                    <th className="p-4">Post Link</th>
                    <th className="p-4">Message</th>
                    <th className="p-4 text-center">Status</th>
                    <th className="p-4 text-right">Moderations</th>
                  </tr>
                </thead>
                <tbody>
                  {comments.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-500 italic">No user comments submitted yet.</td>
                    </tr>
                  ) : (
                    comments.map((c) => (
                      <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="p-4 font-bold text-white">
                          <div>{c.authorName}</div>
                          <div className="text-[10px] text-slate-500 mt-1 font-semibold">{new Date(c.createdAt).toLocaleDateString()}</div>
                        </td>
                        <td className="p-4 text-xs font-mono">/blog/{c.postSlug}</td>
                        <td className="p-4 max-w-xs text-xs text-slate-400 line-clamp-2 mt-2 leading-relaxed">{c.commentText}</td>
                        <td className="p-4 text-center">
                          <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${c.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="p-4 text-right space-x-2">
                          {c.status === 'pending' && (
                            <button 
                              onClick={() => handleCommentStatus(c.id, 'approved')}
                              className="p-1.5 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 rounded transition-colors"
                              title="Approve"
                            >
                              <Icon name="Check" size={12} />
                            </button>
                          )}
                          <button 
                            onClick={() => handleCommentStatus(c.id, 'delete')}
                            className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                            title="Delete"
                          >
                            <Icon name="Trash" size={12} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Section: Settings Manager */}
          {activeSection === 'settings' && (
            <form onSubmit={handleSaveSettings} className="space-y-6 text-left max-w-2xl mx-auto">
              
              <div className="p-6 bg-slate-950/30 border border-white/5 rounded-3xl space-y-6">
                <h4 className="font-bold text-sm uppercase tracking-wider text-slate-400 border-b border-white/5 pb-2">Global Defaults</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-500 text-xs font-bold mb-2 uppercase">Default Author</label>
                    <input 
                      type="text"
                      value={settings.defaultAuthor}
                      onChange={(e) => setSettings(prev => ({ ...prev, defaultAuthor: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs font-bold mb-2 uppercase">Default Category</label>
                    <select
                      value={settings.defaultCategory}
                      onChange={(e) => setSettings(prev => ({ ...prev, defaultCategory: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none"
                    >
                      {categories.map((cat, idx) => (
                        <option key={idx} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-slate-500 text-xs font-bold mb-2 uppercase">Default Meta Description</label>
                  <textarea
                    rows={3}
                    value={settings.defaultMetaDesc}
                    onChange={(e) => setSettings(prev => ({ ...prev, defaultMetaDesc: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-slate-950 border border-white/8 rounded-xl text-white text-sm focus:outline-none"
                  />
                </div>
              </div>

              <div className="p-6 bg-slate-950/30 border border-white/5 rounded-3xl space-y-4">
                <h4 className="font-bold text-sm uppercase tracking-wider text-slate-400 border-b border-white/5 pb-2">Automation Switches</h4>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">Generate URL Slugs automatically from title</span>
                  <input 
                    type="checkbox"
                    checked={settings.autoSlug}
                    onChange={(e) => setSettings(prev => ({ ...prev, autoSlug: e.target.checked }))}
                    className="w-4 h-4 accent-primary"
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">Calculate reading times automatically from word count</span>
                  <input 
                    type="checkbox"
                    checked={settings.autoReadingTime}
                    onChange={(e) => setSettings(prev => ({ ...prev, autoReadingTime: e.target.checked }))}
                    className="w-4 h-4 accent-primary"
                  />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">Trigger automatic build-time sitemap entry updates</span>
                  <input 
                    type="checkbox"
                    checked={settings.autoSitemap}
                    onChange={(e) => setSettings(prev => ({ ...prev, autoSitemap: e.target.checked }))}
                    className="w-4 h-4 accent-primary"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button 
                  type="submit"
                  className="px-6 py-2.5 bg-primary text-white rounded-xl text-xs font-semibold shadow-brand-sm"
                >
                  Save Settings Profile
                </button>
              </div>

            </form>
          )}
        </>
      )}
    </div>
  );
};

export default BlogAdminPanel;
